/**
 * Tarifario de los médicos colaboradores (administración, 7/10/2026).
 *
 * Los precios con los que Colaboradores calcula lo que se factura a la médica
 * de estética y a los cirujanos de fuera: tratamientos (PVP y, desde el
 * 27/10/2026, si van sin IVA y una nota), productos (coste por vial) y
 * cirugías (precio sugerido y si van sin IVA). Hasta el 7/10/2026 el
 * servidor ya dejaba cambiarlos (`/api/colaboradores/catalogos/…`) pero
 * ninguna pantalla lo ofrecía. Los tratamientos se editan también desde el
 * bloque «Tratamientos de colaboradores» del Catálogo de precios
 * (administración, 27/10/2026): el MISMO tarifario, las dos pantallas
 * enseñan y cambian lo mismo.
 *
 * Solo lo abre gestión (el botón «Tarifario» de ColaboradoresPage); el
 * servidor lo vuelve a comprobar (403), rechaza un nombre repetido (409) y lo
 * apunta todo en Control rastreo. Retirar es blando: deja de salir para
 * elegir y lo ya registrado no cambia (cada acto copia su importe al
 * registrarse). Tras cada cambio se recargan los catálogos de la página, para
 * que los desplegables de los actos usen ya lo nuevo.
 *
 * Aquí no hay ni un precio: todo viene de `GET /api/colaboradores/catalogos`.
 *
 * SIN ENSEÑAR NADA COMO VACÍO (revisión del 7/10/2026). Antes, mientras
 * llegaban los catálogos o si el GET fallaba, cada pestaña decía «Aún no hay
 * … en el tarifario», y se leía como un tarifario vacío. Ahora (`estado`,
 * el de la carga de la página): mientras cargan, «Cargando…»; si fallan, un
 * aviso con «Reintentar» y nada más; si falla la recarga tras un cambio, el
 * aviso dice que lo que se ve puede no estar al día. Y si lo que se edita o
 * se retira ya no está (404: otra persona lo retiró), se recarga la lista y
 * se dice.
 *
 * Props: { catalogos, estado ('cargando' | 'listo' | 'error'), onClose,
 *          onCambiado (recarga los catálogos; devuelve si ha podido),
 *          inicial (la pestaña con la que se abre) }
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Pencil, Plus, RotateCw, Search, Trash2 } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import { importeParaCampo, leerImporte, textoEuros } from '../../lib/importeEuros';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

// `sinIva`: el catálogo admite la marca «sin IVA» (exento: el importe no se
// divide entre 1,21 al calcular el reparto); `textoSinIva`, lo que se explica
// bajo la casilla. `nota`: admite un texto libre.
export const CATALOGOS_TARIFARIO = {
  tratamientos: {
    titulo: 'Tratamientos', uno: 'tratamiento', campo: 'pvp', columna: 'PVP',
    etiquetaImporte: 'PVP (€)', anadir: 'Añadir tratamiento', sinIva: true, nota: true,
    textoSinIva: 'Tratamiento exento: el PVP no se divide entre 1,21 al calcular el reparto.',
    anadido: 'Tratamiento añadido', retirado: 'Tratamiento retirado',
  },
  productos: {
    titulo: 'Productos', uno: 'producto', campo: 'coste_vial', columna: 'Coste por vial',
    etiquetaImporte: 'Coste por vial (€)', anadir: 'Añadir producto',
    anadido: 'Producto añadido', retirado: 'Producto retirado',
  },
  cirugias: {
    titulo: 'Cirugías', uno: 'cirugía', campo: 'sugerido', columna: 'Precio sugerido',
    etiquetaImporte: 'Precio sugerido (€)', anadir: 'Añadir cirugía', sinIva: true,
    textoSinIva: 'Intervención exenta: el precio no se divide entre 1,21 al calcular el reparto.',
    anadido: 'Cirugía añadida', retirado: 'Cirugía retirada', femenino: true,
  },
};
const PESTANAS = Object.keys(CATALOGOS_TARIFARIO);

/** Para buscar y ordenar: sin mayúsculas ni acentos. */
const plano = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

function Formulario({ conf, form, setForm, error, guardando, onGuardar, onCancelar }) {
  const pon = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  return (
    <form
      className="rounded-md border border-[rgba(46,110,94,0.35)] bg-[rgba(46,110,94,0.04)] p-3 space-y-3"
      onSubmit={(e) => { e.preventDefault(); onGuardar(); }}
      data-testid="tarifario-form"
    >
      <div className="text-sm font-semibold text-[#13231E]">
        {form.id ? `Editar ${conf.uno}` : conf.anadir}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_11rem] gap-3">
        <div>
          <Label htmlFor="tarifario-nombre">Nombre</Label>
          <Input id="tarifario-nombre" value={form.nombre} onChange={pon('nombre')} maxLength={120}
            autoComplete="off" data-testid="tarifario-form-nombre" />
        </div>
        <div>
          <Label htmlFor="tarifario-importe">{conf.etiquetaImporte}</Label>
          <Input id="tarifario-importe" value={form.importe} onChange={pon('importe')} inputMode="decimal"
            placeholder="0,00" autoComplete="off" className="text-right tabular-nums"
            data-testid="tarifario-form-importe" />
        </div>
      </div>
      {conf.sinIva && (
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={!!form.sin_iva} onChange={pon('sin_iva')}
            data-testid="tarifario-form-sin-iva" />
          <span>
            Sin IVA
            <span className="block text-xs text-muted-foreground">{conf.textoSinIva}</span>
          </span>
        </label>
      )}
      {conf.nota && (
        <div>
          <Label htmlFor="tarifario-nota">Nota</Label>
          <Textarea id="tarifario-nota" rows={3} value={form.nota || ''} onChange={pon('nota')} maxLength={2000}
            data-testid="tarifario-form-nota" />
        </div>
      )}
      {error && <p className="text-sm text-red-700" role="alert" data-testid="tarifario-form-error">{error}</p>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancelar} disabled={guardando}>Cancelar</Button>
        <Button type="submit" disabled={guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
          data-testid="tarifario-form-guardar">
          {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
        </Button>
      </div>
    </form>
  );
}

/** El aviso de que los catálogos no se han podido cargar (o recargar). */
function AvisoSinCargar({ hayDatos, reintentando, onReintentar }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-900"
      role="alert" data-testid="tarifario-error">
      <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
      <p className="flex-1 min-w-[12rem] text-sm">
        {hayDatos
          ? 'No se ha podido recargar el tarifario: lo que ves puede no estar al día.'
          : 'No se ha podido cargar el tarifario. Comprueba la conexión y vuelve a intentarlo.'}
      </p>
      <Button type="button" size="sm" variant="outline" onClick={onReintentar} disabled={reintentando}
        data-testid="tarifario-reintentar">
        {reintentando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1.5" />}
        Reintentar
      </Button>
    </div>
  );
}

export default function TarifarioColaboradoresDialog({
  catalogos, estado = 'listo', onClose, onCambiado, inicial = 'tratamientos',
}) {
  const [cual, setCual] = useState(PESTANAS.includes(inicial) ? inicial : 'tratamientos');
  const [busqueda, setBusqueda] = useState('');
  const [form, setForm] = useState(null);          // {id?, nombre, importe, sin_iva, nota}
  const [errorForm, setErrorForm] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(null);   // id del que se va a retirar
  const [retirando, setRetirando] = useState(false);
  const conf = CATALOGOS_TARIFARIO[cual];
  // Sin catálogos todavía (cargando o con error) no hay lista que enseñar,
  // ni vacía.
  const hayDatos = !!catalogos && typeof catalogos === 'object';
  const cargando = estado === 'cargando';
  const conError = estado === 'error';

  /** Recarga los catálogos; true si ha podido. */
  const recargar = async () => {
    if (!onCambiado) return true;
    return (await onCambiado()) !== false;
  };
  const reintentar = () => { recargar(); };
  /** «Ese tratamiento» / «Esa cirugía». */
  const ese = `${conf.femenino ? 'Esa' : 'Ese'} ${conf.uno}`;
  /** Lo que se editaba o se retiraba ya no está (404): se recarga y se dice. */
  const yaNoEsta = async () => {
    const ok = await recargar();
    toast.error(ok
      ? `${ese} ya no está en el tarifario: lo ha retirado otra persona. Se ha recargado la lista.`
      : `${ese} ya no está en el tarifario: lo ha retirado otra persona. No se ha podido recargar la lista: pulsa «Reintentar».`);
  };
  const avisarSiNoRecarga = (ok) => {
    if (!ok) toast.error('Guardado, pero no se ha podido recargar el tarifario: lo que ves puede no estar al día. Pulsa «Reintentar».');
  };

  const filas = useMemo(() => {
    const q = plano(busqueda);
    return (Array.isArray(catalogos?.[cual]) ? catalogos[cual] : [])
      .filter((d) => !q || plano(d.nombre).includes(q))
      .sort((a, b) => plano(a.nombre).localeCompare(plano(b.nombre), 'es'));
  }, [catalogos, cual, busqueda]);

  const cambiarPestana = (k) => {
    setCual(k); setBusqueda(''); setForm(null); setErrorForm(''); setConfirmando(null);
  };
  const abrirNuevo = () => {
    setConfirmando(null); setErrorForm('');
    setForm({ nombre: busqueda.trim(), importe: '', sin_iva: false, nota: '' });
  };
  const abrirEditar = (d) => {
    setConfirmando(null); setErrorForm('');
    setForm({ id: d.id, nombre: d.nombre || '', importe: importeParaCampo(d[conf.campo]),
      sin_iva: !!d.sin_iva, nota: d.nota || '' });
  };

  const guardar = async () => {
    const nombre = (form.nombre || '').trim();
    if (!nombre) { setErrorForm('Escribe el nombre.'); return; }
    const precio = leerImporte(form.importe);
    if (precio === null || Number.isNaN(precio)) {
      setErrorForm('Escribe el importe en euros, por ejemplo 12,50.');
      return;
    }
    const cuerpo = { nombre, precio };
    if (conf.sinIva) cuerpo.sin_iva = !!form.sin_iva;
    if (conf.nota) cuerpo.nota = (form.nota || '').trim();
    setGuardando(true); setErrorForm('');
    try {
      if (form.id) await api.put(`/api/colaboradores/catalogos/${cual}/${form.id}`, cuerpo);
      else await api.post(`/api/colaboradores/catalogos/${cual}`, cuerpo);
      toast.success(form.id ? 'Cambios guardados' : conf.anadido);
      setForm(null);
      avisarSiNoRecarga(await recargar());
    } catch (e) {
      if (form.id && e?.response?.status === 404) {
        // Otra persona lo retiró con esta pantalla abierta.
        setForm(null); setErrorForm('');
        await yaNoEsta();
      } else {
        setErrorForm(mensajeError(e, 'No se ha podido guardar'));
      }
    } finally {
      setGuardando(false);
    }
  };

  const retirar = async (d) => {
    setRetirando(true);
    try {
      await api.delete(`/api/colaboradores/catalogos/${cual}/${d.id}`);
      toast.success(conf.retirado);
      setConfirmando(null);
      if (form?.id === d.id) setForm(null);
      avisarSiNoRecarga(await recargar());
    } catch (e) {
      if (e?.response?.status === 404) {
        setConfirmando(null);
        if (form?.id === d.id) setForm(null);
        await yaNoEsta();
      } else {
        toast.error(mensajeError(e, 'No se ha podido retirar'));
      }
    } finally {
      setRetirando(false);
    }
  };

  const total = Array.isArray(catalogos?.[cual]) ? catalogos[cual].length : 0;
  const columnas = 3 + (conf.sinIva ? 1 : 0) + (conf.nota ? 1 : 0);
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tarifario-dialogo">
        <DialogHeader>
          <DialogTitle>Tarifario de colaboradores</DialogTitle>
          <DialogDescription>
            Los precios con los que se calcula lo que se factura a los médicos colaboradores. Un cambio vale
            para los actos que se registren a partir de ahora: lo ya registrado conserva su importe.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1 border-b border-border" role="tablist" aria-label="Catálogos del tarifario">
          {PESTANAS.map((k) => {
            const activa = k === cual;
            const n = Array.isArray(catalogos?.[k]) ? catalogos[k].length : 0;
            return (
              <button key={k} type="button" role="tab" aria-selected={activa}
                onClick={() => cambiarPestana(k)} data-testid={`tarifario-pestana-${k}`}
                className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${activa
                  ? 'border-[#2E6E5E] text-[#13231E] font-semibold'
                  : 'border-transparent text-muted-foreground hover:text-[#13231E]'}`}>
                {CATALOGOS_TARIFARIO[k].titulo}
                {/* Sin catálogos, sin cuenta: un «(0)» se leería como vacío. */}
                {hayDatos && <> <span className="text-xs tabular-nums">({n})</span></>}
              </button>
            );
          })}
        </div>

        {/* Buscar y añadir, solo con la lista delante. */}
        {hayDatos && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[12rem]">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="pl-8"
                placeholder={`Buscar ${conf.uno}…`} aria-label={`Buscar ${conf.uno}`}
                data-testid="tarifario-buscar" />
            </div>
            {!form && (
              <Button type="button" size="sm" onClick={abrirNuevo} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                data-testid="tarifario-anadir">
                <Plus className="w-4 h-4 mr-1" /> {conf.anadir}
              </Button>
            )}
          </div>
        )}

        {conError && (
          <AvisoSinCargar hayDatos={hayDatos} reintentando={cargando} onReintentar={reintentar} />
        )}

        {form && (
          <Formulario conf={conf} form={form} setForm={setForm} error={errorForm} guardando={guardando}
            onGuardar={guardar} onCancelar={() => { setForm(null); setErrorForm(''); }} />
        )}

        {!hayDatos && !conError && (
          <p className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
            role="status" data-testid="tarifario-cargando">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Cargando…
          </p>
        )}

        {hayDatos && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`tarifario-tabla-${cual}`}>
              <thead className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3 font-semibold">Nombre</th>
                  <th className="py-2 px-3 font-semibold text-right whitespace-nowrap">{conf.columna}</th>
                  {conf.sinIva && <th className="py-2 px-3 font-semibold whitespace-nowrap">IVA</th>}
                  {conf.nota && <th className="py-2 px-3 font-semibold">Nota</th>}
                  <th className="py-2 pl-3 w-px" />
                </tr>
              </thead>
              <tbody>
                {filas.map((d) => (confirmando === d.id ? (
                  <tr key={d.id} className="border-b border-border/60" data-testid="tarifario-fila">
                    <td colSpan={columnas} className="py-2">
                      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950"
                        role="alertdialog" aria-label={`Retirar ${d.nombre}`} data-testid="tarifario-confirmar">
                        <p className="text-sm">
                          <span className="font-semibold">¿Retirar «{d.nombre}»?</span>{' '}
                          Dejará de salir para elegir en los actos nuevos. Lo ya registrado y facturado con
                          {conf.femenino ? ' ella' : ' él'} no cambia: conserva su nombre y su importe.
                        </p>
                        <div className="mt-2 flex flex-wrap justify-end gap-2">
                          <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmando(null)}
                            disabled={retirando}>Cancelar</Button>
                          <Button type="button" size="sm" variant="destructive" onClick={() => retirar(d)}
                            disabled={retirando} data-testid="tarifario-confirmar-retirar">
                            {retirando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Retirar
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={d.id} className="border-b border-border/60" data-testid="tarifario-fila">
                    <td className="py-2 pr-3 font-medium text-[#13231E]" data-testid="tarifario-fila-nombre">{d.nombre}</td>
                    <td className="py-2 px-3 text-right tabular-nums whitespace-nowrap" data-testid="tarifario-fila-importe">
                      {textoEuros(d[conf.campo])}
                    </td>
                    {conf.sinIva && (
                      <td className="py-2 px-3 text-xs whitespace-nowrap">{d.sin_iva ? 'Sin IVA' : 'Con IVA'}</td>
                    )}
                    {conf.nota && (
                      <td className="py-2 px-3 text-xs text-muted-foreground whitespace-pre-line"
                        data-testid="tarifario-fila-nota">{d.nota || ''}</td>
                    )}
                    <td className="py-1 pl-3">
                      <div className="flex justify-end gap-0.5">
                        <Button type="button" size="sm" variant="ghost" onClick={() => abrirEditar(d)}
                          title="Editar" aria-label={`Editar ${d.nombre}`} data-testid="tarifario-editar">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button type="button" size="sm" variant="ghost" className="text-destructive"
                          onClick={() => setConfirmando(d.id)} title="Retirar" aria-label={`Retirar ${d.nombre}`}
                          data-testid="tarifario-retirar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )))}
                {filas.length === 0 && (
                  <tr>
                    <td colSpan={columnas} className="py-6 text-center text-sm text-muted-foreground" data-testid="tarifario-vacio">
                      {total === 0
                        ? `Aún no hay ${conf.titulo.toLowerCase()} en el tarifario.`
                        : `${conf.femenino ? 'Ninguna' : 'Ningún'} ${conf.uno} coincide con «${busqueda.trim()}».`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
